import {
  HEART_CENTRAL_ROLES,
  type HeartCentralMovement,
  type HeartCentralPhase,
  type HeartCentralPrivateProjection,
  type HeartCentralPrivateReport,
  type HeartCentralPublicProjection,
  type HeartCentralRole,
} from "../../../cloudflare/src/game/heart-central-runtime";

export interface HeartCentralSnapshot {
  readonly public: HeartCentralPublicProjection;
  readonly self: HeartCentralPrivateProjection;
  readonly command?: {
    readonly replayed: boolean;
    readonly revision: number;
  };
}

export interface HeartCentralPresentation {
  readonly title: string;
  readonly phaseLabel: string;
  readonly movementLabel: string;
  readonly summary: string;
  readonly nextAction: string;
  readonly assignedRoleLabels: readonly string[];
  readonly completedRoleLabels: readonly string[];
  readonly availableRoles: readonly {
    readonly role: HeartCentralRole;
    readonly label: string;
  }[];
  readonly reportTitle: string | null;
  readonly reportDetail: string | null;
}

const ROLE_LABELS: Readonly<Record<HeartCentralRole, string>> = Object.freeze({
  observer: "Observer les fractures",
  purifier: "Purifier la marque",
  ravitailler: "Ravitailler la Couronne",
  tenir: "Tenir les six ancrages",
  combattre: "Frapper le Régisseur",
});

const PHASE_LABELS: Readonly<Record<HeartCentralPhase, string>> = Object.freeze({
  active: "Affrontement central en cours",
  contained: "Régisseur contenu",
  repelled: "Tentative centrale repoussée",
});

const MOVEMENT_LABELS: Readonly<Record<HeartCentralMovement, string>> = Object.freeze({
  manifestation: "Manifestation",
  fracture: "Fracture",
  contention: "Contention",
});

function fail(): never {
  throw new Error("Projection du Régisseur invalide.");
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail();
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): void {
  const actual = Object.keys(value).sort().join(",");
  const expected = [...keys].sort().join(",");
  if (actual !== expected) fail();
}

function integer(value: unknown, minimum = 0): number {
  if (!Number.isSafeInteger(value) || Number(value) < minimum) fail();
  return Number(value);
}

function nullableTimestamp(value: unknown): number | null {
  return value === null ? null : integer(value, 1);
}

function phase(value: unknown): HeartCentralPhase {
  if (value !== "active" && value !== "contained" && value !== "repelled") fail();
  return value;
}

function movement(value: unknown): HeartCentralMovement {
  if (value !== "manifestation" && value !== "fracture" && value !== "contention") fail();
  return value;
}

function role(value: unknown): HeartCentralRole {
  if (!HEART_CENTRAL_ROLES.includes(value as HeartCentralRole)) fail();
  return value as HeartCentralRole;
}

function roles(value: unknown): readonly HeartCentralRole[] {
  if (!Array.isArray(value)) fail();
  const parsed = value.map(role);
  if (new Set(parsed).size !== parsed.length) fail();
  return Object.freeze(parsed);
}

function parseReport(value: unknown): HeartCentralPrivateReport | null {
  if (value === null) return null;
  const source = record(value);
  exactKeys(source, [
    "schema", "reportId", "attempt", "outcome", "assignedRoles", "completedRoles", "settledAtMs",
  ]);
  if (
    source.schema !== "vaelor-heart-central-private-report/v1" ||
    typeof source.reportId !== "string" ||
    source.reportId.length < 1 ||
    source.reportId.length > 128 ||
    (source.outcome !== "contained" && source.outcome !== "repelled")
  ) fail();
  const assignedRoles = roles(source.assignedRoles);
  const completedRoles = roles(source.completedRoles);
  if (completedRoles.some((currentRole) => !assignedRoles.includes(currentRole))) fail();
  return Object.freeze({
    schema: "vaelor-heart-central-private-report/v1",
    reportId: source.reportId,
    attempt: integer(source.attempt, 1),
    outcome: source.outcome,
    assignedRoles,
    completedRoles,
    settledAtMs: integer(source.settledAtMs, 1),
  });
}

function parsePublic(value: unknown): HeartCentralPublicProjection {
  const source = record(value);
  exactKeys(source, [
    "schema", "phase", "movement", "attempt", "functions", "anchors", "contentionEndsAtMs", "verdict", "nextAction",
  ]);
  if (
    source.schema !== "vaelor-heart-central-public/v1" ||
    !Array.isArray(source.functions) ||
    typeof source.nextAction !== "string" ||
    source.nextAction.length < 1 ||
    source.nextAction.length > 512
  ) fail();
  const parsedFunctions = source.functions.map((candidate) => {
    const item = record(candidate);
    exactKeys(item, ["role", "status"]);
    if (item.status !== "pending" && item.status !== "complete") fail();
    return Object.freeze({ role: role(item.role), status: item.status });
  });
  if (
    parsedFunctions.length !== HEART_CENTRAL_ROLES.length ||
    HEART_CENTRAL_ROLES.some((currentRole) => parsedFunctions.filter((item) => item.role === currentRole).length !== 1)
  ) fail();
  const anchors = record(source.anchors);
  exactKeys(anchors, ["activeCount", "requiredCount", "toleratedBreaks"]);
  if (!Number.isSafeInteger(anchors.activeCount) || Number(anchors.activeCount) < 0 || Number(anchors.activeCount) > 6 ||
      anchors.requiredCount !== 6 || anchors.toleratedBreaks !== 2) fail();
  const parsedPhase = phase(source.phase);
  const verdict = source.verdict;
  if (
    (verdict !== "pending" && verdict !== "contained" && verdict !== "repelled") ||
    (parsedPhase === "active" ? verdict !== "pending" : verdict !== parsedPhase)
  ) fail();
  return Object.freeze({
    schema: "vaelor-heart-central-public/v1",
    phase: parsedPhase,
    movement: movement(source.movement),
    attempt: integer(source.attempt, 1),
    functions: Object.freeze(parsedFunctions),
    anchors: Object.freeze({ activeCount: Number(anchors.activeCount), requiredCount: 6, toleratedBreaks: 2 }),
    contentionEndsAtMs: nullableTimestamp(source.contentionEndsAtMs),
    verdict,
    nextAction: source.nextAction,
  });
}

function parseSelf(value: unknown): HeartCentralPrivateProjection {
  const source = record(value);
  exactKeys(source, [
    "schema", "phase", "movement", "attempt", "assignedRoles", "completedRoles", "availableRoles",
    "canSettle", "canReopen", "report", "nextAction",
  ]);
  if (
    source.schema !== "vaelor-heart-central-private/v1" ||
    typeof source.canSettle !== "boolean" ||
    typeof source.canReopen !== "boolean" ||
    typeof source.nextAction !== "string" ||
    source.nextAction.length < 1 ||
    source.nextAction.length > 512
  ) fail();
  const parsedPhase = phase(source.phase);
  const assignedRoles = roles(source.assignedRoles);
  const completedRoles = roles(source.completedRoles);
  const availableRoles = roles(source.availableRoles);
  if (
    completedRoles.some((currentRole) => !assignedRoles.includes(currentRole)) ||
    availableRoles.some((currentRole) => !assignedRoles.includes(currentRole) || completedRoles.includes(currentRole)) ||
    (parsedPhase !== "active" && availableRoles.length !== 0) ||
    source.canReopen !== false
  ) fail();
  return Object.freeze({
    schema: "vaelor-heart-central-private/v1",
    phase: parsedPhase,
    movement: movement(source.movement),
    attempt: integer(source.attempt, 1),
    assignedRoles,
    completedRoles,
    availableRoles,
    canSettle: source.canSettle,
    canReopen: source.canReopen,
    report: parseReport(source.report),
    nextAction: source.nextAction,
  });
}

export function parseHeartCentralSnapshot(value: unknown): HeartCentralSnapshot {
  const source = record(value);
  const keys = Object.keys(source).sort().join(",");
  if (keys !== "public,self" && keys !== "command,public,self") fail();
  const publicProjection = parsePublic(source.public);
  const selfProjection = parseSelf(source.self);
  if (
    publicProjection.phase !== selfProjection.phase ||
    publicProjection.movement !== selfProjection.movement ||
    publicProjection.attempt !== selfProjection.attempt
  ) fail();
  let command: HeartCentralSnapshot["command"];
  if (source.command !== undefined) {
    const commandSource = record(source.command);
    exactKeys(commandSource, ["replayed", "revision"]);
    if (typeof commandSource.replayed !== "boolean") fail();
    command = Object.freeze({
      replayed: commandSource.replayed,
      revision: integer(commandSource.revision, 1),
    });
  }
  return Object.freeze({
    public: publicProjection,
    self: selfProjection,
    ...(command ? { command } : {}),
  });
}

export function heartCentralRoleLabel(value: HeartCentralRole): string {
  return ROLE_LABELS[value];
}

export function presentHeartCentral(snapshot: HeartCentralSnapshot): HeartCentralPresentation {
  const { public: publicProjection, self } = snapshot;
  const completed = publicProjection.functions.filter((item) => item.status === "complete").length;
  const title = PHASE_LABELS[publicProjection.phase];
  const summary = publicProjection.phase === "contained"
    ? "Les cinq fonctions ont contenu le Régisseur. La Couronne tient, mais le Trône reste fermé."
    : publicProjection.phase === "repelled"
      ? "La Contention a cédé. Une nouvelle tentative volontaire doit être scellée depuis la Couronne exposée."
      : `${completed} fonction${completed > 1 ? "s" : ""} sur 5 accomplie${completed > 1 ? "s" : ""}. Les six ancrages restent sous autorité du serveur.`;
  const reportTitle = self.report === null
    ? null
    : self.report.outcome === "contained"
      ? "Ton rapport privé · Régisseur contenu"
      : "Ton rapport privé · tentative repoussée";
  const reportDetail = self.report === null
    ? null
    : `${self.report.completedRoles.length} de tes ${self.report.assignedRoles.length} fonction${self.report.assignedRoles.length > 1 ? "s" : ""} attribuée${self.report.assignedRoles.length > 1 ? "s" : ""} accomplie${self.report.completedRoles.length > 1 ? "s" : ""}.`;
  return Object.freeze({
    title,
    phaseLabel: PHASE_LABELS[publicProjection.phase],
    movementLabel: MOVEMENT_LABELS[publicProjection.movement],
    summary,
    nextAction: self.nextAction,
    assignedRoleLabels: Object.freeze(self.assignedRoles.map(heartCentralRoleLabel)),
    completedRoleLabels: Object.freeze(self.completedRoles.map(heartCentralRoleLabel)),
    availableRoles: Object.freeze(self.availableRoles.map((currentRole) => ({
      role: currentRole,
      label: heartCentralRoleLabel(currentRole),
    }))),
    reportTitle,
    reportDetail,
  });
}
